import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { ImageIcon, Loader2, Trash2, Upload } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { FichaForm } from '@/components/fichas/ficha-form'
import {
  deleteFichaImagen,
  getFicha,
  publishFicha,
  unpublishFicha,
  updateFicha,
  uploadFichaImagen,
  type ReferenceFichaDto,
} from '@/features/fichas/api'
import { fichasCopy } from '@/features/fichas/copy'
import { fromDto, isPublishableFicha, toContentBody } from '@/features/fichas/content'
import { useSession } from '@/features/auth/session'
import { fichaContentSchema, type FichaCata } from '@escuela/ficha-domain'

export function FichaEditPage(): ReactNode {
  const navigate = useNavigate()
  const params = useParams<{ id: string }>()
  const id = params.id ?? ''
  const queryClient = useQueryClient()
  const { user } = useSession()
  // Permiso de publicar: admin siempre; docente solo si un admin se lo otorgo.
  const canPublish = user?.rol === 'ADMIN' || user?.canPublishFichas === true

  const { data: dto, isLoading, isError, error } = useQuery({
    queryKey: ['fichas', id],
    queryFn: () => getFicha(id),
    enabled: Boolean(id),
  })

  const [ficha, setFicha] = useState<FichaCata | null>(null)
  useEffect(() => {
    if (dto) setFicha(fromDto(dto))
  }, [dto])

  const content = useMemo(() => (ficha ? toContentBody(ficha) : null), [ficha])

  const updateMutation = useMutation({
    mutationFn: () => {
      if (!content) throw new Error('Ficha no cargada')
      return updateFicha(id, content)
    },
    onSuccess: (updated) => {
      toast.success(fichasCopy.toasts.updated)
      queryClient.setQueryData(['fichas', id], updated)
      void queryClient.invalidateQueries({ queryKey: ['fichas', 'list'] })
      navigate('/dashboard/fichas')
    },
    onError: (err: unknown) => {
      toast.error(err instanceof Error ? err.message : fichasCopy.toasts.updateError)
    },
  })

  const statusMutation = useMutation({
    mutationFn: (publish: boolean) => (publish ? publishFicha(id) : unpublishFicha(id)),
    onSuccess: (updated: ReferenceFichaDto, publish) => {
      toast.success(publish ? fichasCopy.toasts.published : fichasCopy.toasts.unpublished)
      queryClient.setQueryData(['fichas', id], updated)
      void queryClient.invalidateQueries({ queryKey: ['fichas'] })
    },
    onError: (err: unknown) => {
      toast.error(err instanceof Error ? err.message : fichasCopy.toasts.updateError)
    },
  })

  const fileInputRef = useRef<HTMLInputElement | null>(null)

  const imageMutation = useMutation({
    mutationFn: (file: File) => uploadFichaImagen(id, file),
    onSuccess: (updated: ReferenceFichaDto) => {
      toast.success(fichasCopy.toasts.imageUploaded)
      queryClient.setQueryData(['fichas', id], updated)
      void queryClient.invalidateQueries({ queryKey: ['fichas'] })
    },
    onError: (err: unknown) => {
      toast.error(err instanceof Error ? err.message : fichasCopy.toasts.imageError)
    },
  })

  const removeImageMutation = useMutation({
    mutationFn: () => deleteFichaImagen(id),
    onSuccess: (updated: ReferenceFichaDto) => {
      toast.success(fichasCopy.toasts.imageRemoved)
      queryClient.setQueryData(['fichas', id], updated)
      void queryClient.invalidateQueries({ queryKey: ['fichas'] })
    },
    onError: (err: unknown) => {
      toast.error(err instanceof Error ? err.message : fichasCopy.toasts.imageRemoveError)
    },
  })

  const ALLOWED_IMAGE = ['image/jpeg', 'image/png', 'image/webp']
  const MAX_IMAGE_BYTES = 5 * 1024 * 1024

  const handlePickImage = (ev: React.ChangeEvent<HTMLInputElement>): void => {
    const file = ev.target.files?.[0]
    ev.target.value = '' // permite re-elegir el mismo archivo
    if (!file) return
    if (!ALLOWED_IMAGE.includes(file.type)) {
      toast.error(fichasCopy.toasts.imageBadType)
      return
    }
    if (file.size > MAX_IMAGE_BYTES) {
      toast.error(fichasCopy.toasts.imageTooLarge)
      return
    }
    imageMutation.mutate(file)
  }

  const busy =
    updateMutation.isPending ||
    statusMutation.isPending ||
    imageMutation.isPending ||
    removeImageMutation.isPending

  const handleSave = (): void => {
    if (!content) return
    const parsed = fichaContentSchema.safeParse(content)
    if (!parsed.success) {
      toast.error(fichasCopy.toasts.invalid)
      return
    }
    if (ficha && !isPublishableFicha(ficha)) {
      toast.warning(fichasCopy.toasts.draftIncomplete)
    }
    updateMutation.mutate()
  }

  const handlePublish = (): void => {
    if (!ficha || !isPublishableFicha(ficha)) {
      toast.error(fichasCopy.toasts.notPublishable)
      return
    }
    statusMutation.mutate(true)
  }

  if (isLoading || !ficha || !dto) {
    return (
      <div className="flex flex-col gap-4">
        <h1 className="text-2xl font-semibold tracking-tight">{fichasCopy.editTitle}</h1>
        {isError ? (
          <div className="rounded-lg border bg-card p-6">
            <p className="text-sm text-destructive">
              {error instanceof Error ? error.message : fichasCopy.toasts.loadError}
            </p>
            <div className="mt-4">
              <Button variant="outline" onClick={() => navigate('/dashboard/fichas')}>
                Volver
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Cargando...
          </div>
        )}
      </div>
    )
  }

  const published = dto.status === 'PUBLISHED'
  const publishable = isPublishableFicha(ficha)

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{fichasCopy.editTitle}</h1>
          <p className="text-sm text-muted-foreground">{fichasCopy.editDescription}</p>
        </div>
        {published ? (
          <Badge variant="success">{fichasCopy.status.published}</Badge>
        ) : (
          <Badge variant="muted">{fichasCopy.status.draft}</Badge>
        )}
      </div>

      {/* Foto del vino: opcional, se muestra en la app cuando la ficha se publica. */}
      <div className="flex flex-col gap-3 rounded-lg border bg-card p-4">
        <div>
          <h2 className="text-sm font-semibold">{fichasCopy.imagen.title}</h2>
          <p className="text-xs text-muted-foreground">{fichasCopy.imagen.description}</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex h-24 w-24 shrink-0 items-center justify-center overflow-hidden rounded-lg border bg-muted">
            {dto.imagenUrl ? (
              <img src={dto.imagenUrl} alt={dto.vino} className="h-full w-full object-cover" />
            ) : (
              <ImageIcon className="h-8 w-8 text-muted-foreground" />
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              onChange={handlePickImage}
            />
            <Button
              variant="outline"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={busy}
            >
              {imageMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Upload className="h-4 w-4" />
              )}
              {dto.imagenUrl ? fichasCopy.imagen.replace : fichasCopy.imagen.upload}
            </Button>
            {dto.imagenUrl ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => removeImageMutation.mutate()}
                disabled={busy}
              >
                {removeImageMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Trash2 className="h-4 w-4" />
                )}
                {fichasCopy.imagen.remove}
              </Button>
            ) : null}
          </div>
        </div>
      </div>

      <FichaForm value={ficha} onChange={setFicha} />

      <div className="sticky bottom-0 flex flex-wrap items-center justify-end gap-2 border-t bg-background/95 py-4 backdrop-blur">
        <Button variant="outline" onClick={() => navigate('/dashboard/fichas')} disabled={busy}>
          {fichasCopy.actions.cancel}
        </Button>
        {!canPublish ? null : published ? (
          <Button
            variant="secondary"
            onClick={() => statusMutation.mutate(false)}
            disabled={busy}
          >
            {statusMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {fichasCopy.actions.unpublish}
          </Button>
        ) : (
          <div className="flex flex-col items-end gap-1">
            <Button
              variant="secondary"
              onClick={handlePublish}
              disabled={busy || !publishable}
            >
              {statusMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {fichasCopy.actions.publish}
            </Button>
            {!publishable ? (
              <p className="max-w-xs text-right text-xs text-muted-foreground">
                {fichasCopy.toasts.notPublishable}
              </p>
            ) : null}
          </div>
        )}
        <Button onClick={handleSave} disabled={busy}>
          {updateMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          {fichasCopy.actions.saveChanges}
        </Button>
      </div>
    </div>
  )
}
